import { createFileRoute } from '@tanstack/react-router';
import { CertificationInfoCard } from '@/app/(client)/certifications/-components/CertificationInfoCard';
import { CertificationStylesSection } from '@/app/(client)/certifications/-components/CertificationStylesSection';
import { CertificationComments } from '@/components/shared/CertificationComments';
import { FieldCard } from '@/components/shared/FieldLayout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { AdminCertificationHeader } from '../../-components/AdminCertificationHeader';
import { AdminCertificationMeta } from '../../-components/AdminCertificationMeta';
import {
  adminCertificationQueryOptions,
  useAdminCertificationDetail,
} from '../../-hooks/useAdminCertificationDetail';

/** The two types that carry linked styles, as the customer page has it. */
const STYLES_TYPES = new Set(['PRODUCT', 'TRANSACTION']);

export const Route = createFileRoute('/management/certifications/(pages)/$id/')(
  {
    loader: ({ params }) =>
      queryClient
        .ensureQueryData(adminCertificationQueryOptions(params.id))
        // A failed prefetch must not blank the page — the component's own
        // query re-runs and surfaces the error inline.
        .catch(() => undefined),
    component: AdminCertificationDetailPage,
  },
);

function AdminCertificationDetailPage() {
  const { id } = Route.useParams();
  const { getTranslation: t } = useTranslator({
    page: 'managementCertifications',
  });
  const { getTranslation: tDetail } = useTranslator({
    page: 'certificationDetail',
  });
  const detail = useAdminCertificationDetail(id);

  if (detail.isLoading) {
    return (
      <div className='flex flex-col flex-1 min-h-0 overflow-y-auto p-6 gap-4 animate-pulse'>
        <div className='h-24 rounded-lg bg-muted' />
        <div className='h-48 rounded-lg bg-muted' />
      </div>
    );
  }

  if (!detail.cert) {
    return (
      <div className='p-6'>
        <Alert variant='destructive' className='py-2.5'>
          <AlertDescription>{t('detail.notFound')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const cert = detail.cert;
  const isArchived = Boolean(cert.isArchived);
  const hasStyles = cert.type ? STYLES_TYPES.has(cert.type) : false;

  return (
    // Scrolls as one page, like the customer detail: the header goes with the
    // content rather than pinning a short viewport in place.
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      <AdminCertificationHeader
        cert={cert}
        pendingVerdict={detail.pendingVerdict ?? null}
        onVerdict={verdict => void detail.setVerdict(verdict)}
      />

      <div className='flex flex-col gap-6 p-4 @md:p-6'>
        <div className='flex flex-col gap-3'>
          {/* Card and thread side by side above the styles, the layout the
              customer page uses — the same certificate, read the same way. */}
          <div className='grid grid-cols-1 @md:grid-cols-2 gap-4 items-stretch'>
            <CertificationInfoCard
              cert={cert}
              isPending={detail.isSaving}
              onUpdate={patch => void detail.update(patch)}
              onViewFile={detail.viewFile}
              // Management keeps file edit access; users are read-only
              // (product decision, DELTA-2543 2026-09-21).
              canEditFile
              showOfficialName
              isArchived={isArchived}
              t={tDetail}
              className='h-full'
              // Continues the same list: the audit facts are fields of this
              // record, not a second card repeating its shape.
              extraRows={<AdminCertificationMeta cert={cert} />}
            />
            <CertificationComments
              comments={detail.comments}
              isLoading={detail.isCommentsLoading}
              isSending={detail.isAddingComment}
              isArchived={isArchived}
              onSend={async content => {
                await detail.addComment(content);
              }}
            />
          </div>

          {hasStyles && (
            // `FieldCard title` + `bordered={false}`, the same pairing
            // `UserCompaniesCard` uses: the card primitive owns the frame,
            // the title and their padding, and the table stops drawing its own
            // outline on top of it.
            <FieldCard
              title={tDetail('certificationDetail.fields.linkedStyles')}
            >
              {/* `min-h` gives the table a floor: `DataTable`'s root is
                  `flex-1 min-h-0` and sizes to the height it is given, so
                  inside a content-height card a short list collapsed to a
                  couple of rows. Same reasoning as the users detail card. */}
              <div className='flex flex-col min-h-[520px]'>
                <CertificationStylesSection
                  scope='admin'
                  bordered={false}
                  hideTitle
                  certId={cert.id}
                  companyId={cert.companyId}
                  standardId={cert.standardId}
                  {...(cert.issueDate ? { issueDate: cert.issueDate } : {})}
                  {...(cert.expirationDate
                    ? { expirationDate: cert.expirationDate }
                    : {})}
                  initialStyleIds={cert.certificateDependencies?.products ?? []}
                  isPending={detail.isSaving}
                  onSave={patch => void detail.update(patch)}
                  isArchived={isArchived}
                  t={tDetail}
                />
              </div>
            </FieldCard>
          )}
        </div>
      </div>
    </div>
  );
}
